import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandProvider } from './public/BrandContext';
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt, registerServiceWorker } from './components/PWAInstallPrompt';
import { queryClient } from './lib/queryClient';
import './App.css';

// Initialize i18n
import './i18n';

// Register Service Worker for PWA
registerServiceWorker();

// Pages - route-level code splitting: a role only downloads the screens it can open
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const BookingFormPage = lazy(() => import('./pages/BookingFormPage'));
const BookingDetailPage = lazy(() => import('./pages/BookingDetailPage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const ListingEditorPage = lazy(() => import('./pages/ListingEditorPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const PropertyCalendarPage = lazy(() => import('./pages/PropertyCalendarPage'));
const CaretakerPortalPage = lazy(() => import('./pages/CaretakerPortalPage'));
const DailyOperationsPage = lazy(() => import('./pages/DailyOperationsPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const TaskDetailPage = lazy(() => import('./pages/TaskDetailPage'));
const AdminMigrationPage = lazy(() => import('./pages/AdminMigrationPage'));
const AdminArchivingPage = lazy(() => import('./pages/AdminArchivingPage'));
const AdminIcalHealthPage = lazy(() => import('./pages/AdminIcalHealthPage'));
const AdminReviewsPage = lazy(() => import('./pages/AdminReviewsPage'));
const AdminChannelManagerPage = lazy(() => import('./pages/AdminChannelManagerPage'));
const AdminInboxPage = lazy(() => import('./pages/AdminInboxPage'));
const StaffPortalPage = lazy(() => import('./pages/StaffPortalPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// Public customer-facing website (separate bundle from the admin app)
const PublicHomePage = lazy(() => import('./public/pages/HomePage'));
const PublicStayDetailPage = lazy(() => import('./public/pages/StayDetailPage'));
const PublicBookingPage = lazy(() => import('./public/pages/BookingPage'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect caretakers to their portal
    if (user.role === 'caretaker') {
      return <Navigate to="/caretaker" replace />;
    }
    // Redirect staff to their portal
    if (user.role === 'staff') {
      return <Navigate to="/staff" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'caretaker') {
      return <Navigate to="/caretaker" replace />;
    }
    if (user.role === 'staff') {
      return <Navigate to="/staff" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // Read the fragment from useLocation (reactive) - handles the Google sign-in
  // callback BEFORE any route/ProtectedRoute renders.
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Team entry point — the shareable link caretakers/staff install the ops app from */}
      <Route path="/team" element={<Navigate to="/login" replace />} />

      {/* Admin Routes - revenue dashboard & bookings module are admin only */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BookingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings/new"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BookingFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings/:id"
        element={
          <ProtectedRoute>
            <BookingDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings/:id/edit"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BookingFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <PropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties/:propertyId/listing"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ListingEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LocationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar/:propertyId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <PropertyCalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-operations"
        element={
          <ProtectedRoute>
            <DailyOperationsPage />
          </ProtectedRoute>
        }
      />

      {/* Tasks */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/:id"
        element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Caretaker Portal */}
      <Route
        path="/caretaker"
        element={
          <ProtectedRoute allowedRoles={['caretaker']}>
            <CaretakerPortalPage />
          </ProtectedRoute>
        }
      />

      {/* Staff Portal */}
      <Route
        path="/staff"
        element={
          <ProtectedRoute allowedRoles={['staff']}>
            <StaffPortalPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Migration */}
      <Route
        path="/admin/migration"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminMigrationPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Data Archiving */}
      <Route
        path="/admin/archiving"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminArchivingPage />
          </ProtectedRoute>
        }
      />

      {/* Admin iCal Sync Health */}
      <Route
        path="/admin/ical-health"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminIcalHealthPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Guest Reviews importer */}
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminReviewsPage />
          </ProtectedRoute>
        }
      />

      {/* Aiosell Channel Manager */}
      <Route
        path="/admin/channel-manager"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminChannelManagerPage />
          </ProtectedRoute>
        }
      />

      {/* Old Aiosell screens are now tabs on the Channel Manager — keep the links working */}
      <Route
        path="/admin/aiosell/certification"
        element={<Navigate to="/admin/channel-manager?tab=testing" replace />}
      />
      <Route
        path="/admin/aiosell/go-live"
        element={<Navigate to="/admin/channel-manager?tab=setup" replace />}
      />
      <Route
        path="/admin/aiosell/live"
        element={<Navigate to="/admin/channel-manager?tab=setup&env=live" replace />}
      />
      <Route
        path="/admin/aiosell/:environment"
        element={<Navigate to="/admin/channel-manager?tab=setup&env=sandbox" replace />}
      />

      {/* Unified OTA guest inbox */}
      <Route
        path="/admin/inbox"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminInboxPage />
          </ProtectedRoute>
        }
      />


      {/* Public customer-facing website — no login required */}      <Route
        path="/"
        element={
          <BrandProvider>
            <PublicHomePage />
          </BrandProvider>
        }
      />
      <Route
        path="/stay"
        element={
          <BrandProvider>
            <PublicHomePage />
          </BrandProvider>
        }
      />
      <Route
        path="/stay/:slug"
        element={
          <BrandProvider>
            <PublicStayDetailPage />
          </BrandProvider>
        }
      />
      <Route
        path="/book/:slug"
        element={
          <BrandProvider>
            <PublicBookingPage />
          </BrandProvider>
        }
      />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <PWAInstallPrompt />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
